import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import HomeIcon from "@mui/icons-material/Home";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import SearchOffRoundedIcon from "@mui/icons-material/SearchOffRounded";
import {
  M,
  TYPE,
  marketingBtnPrimarySx,
  marketingBtnSecondaryLightSx,
} from "../../constants/marketingTokens";

const LINK_SETS = {
  public: [
    { key: "individualLogin", path: "/individual-login" },
    { key: "corporateLogin", path: "/corporate-login" },
    { key: "about", path: "/about" },
    { key: "contact", path: "/contact" },
  ],
  doctor: [
    { key: "files", path: "/files" },
    { key: "patients", path: "/patients" },
    { key: "team", path: "/team" },
    { key: "profile", path: "/profile" },
  ],
  tenant: [
    { key: "doctors", path: "/tenant/doctors" },
    { key: "patients", path: "/tenant/patients" },
    { key: "profile", path: "/tenant/profile" },
    { key: "permissionGroups", path: "/tenant/permission-groups" },
  ],
};

const centeredText = {
  width: "100%",
  textAlign: "center",
};

export default function NotFoundPageContent({ variant = "public" }) {
  const navigate = useNavigate();
  const { t } = useTranslation("notFound");

  const primaryAction =
    variant === "public"
      ? { label: t("goHome"), path: "/", icon: <HomeIcon /> }
      : {
          label: t("goDashboard"),
          path: variant === "tenant" ? "/tenant/dashboard" : "/dashboard",
          icon: <HomeIcon />,
        };

  return (
    <Box
      sx={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        textAlign: "center",
        py: { xs: 0.5, sm: 1 },
      }}
    >
      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          mb: { xs: 2.5, sm: 3 },
          pt: { xs: 1, sm: 2 },
        }}
      >
        <Box
          aria-hidden
          sx={{
            position: "absolute",
            top: "42%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            width: { xs: 200, sm: 260 },
            height: { xs: 200, sm: 260 },
            borderRadius: "50%",
            background: `radial-gradient(circle, ${M.primaryDim} 0%, rgba(29, 184, 160, 0.06) 60%, transparent 75%)`,
            pointerEvents: "none",
          }}
        />

        <Box
          sx={{
            position: "relative",
            width: { xs: 88, sm: 100 },
            height: { xs: 88, sm: 100 },
            borderRadius: "50%",
            bgcolor: M.white,
            border: `2px solid ${M.primaryDim}`,
            boxShadow: `0 12px 32px ${M.primary}20`,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            mb: 2,
          }}
        >
          <SearchOffRoundedIcon
            sx={{ fontSize: { xs: 44, sm: 50 }, color: M.primary }}
          />
        </Box>

        <Typography
          component="p"
          sx={{
            ...centeredText,
            m: 0,
            fontSize: { xs: "4.75rem", sm: "6.25rem" },
            fontWeight: 900,
            lineHeight: 1,
            letterSpacing: "-0.03em",
            background: `linear-gradient(135deg, ${M.primary} 0%, ${M.primaryDark} 100%)`,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {t("code")}
        </Typography>
      </Box>

      <Box
        sx={{
          width: "100%",
          maxWidth: 440,
          mx: "auto",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        <Typography
          sx={{
            ...TYPE.sectionH2,
            ...centeredText,
            fontSize: { xs: "1.45rem", sm: "1.75rem" },
            fontWeight: 800,
            color: M.dark,
            mb: 1.25,
          }}
        >
          {t("title")}
        </Typography>

        <Typography
          sx={{
            ...TYPE.body,
            ...centeredText,
            fontSize: { xs: "0.975rem", sm: "1.025rem" },
            color: M.muted,
            mb: { xs: 3, sm: 3.5 },
            lineHeight: 1.65,
          }}
        >
          {t("description")}
        </Typography>

        <Box
          sx={{
            width: "100%",
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            gap: 1.25,
            justifyContent: "center",
            alignItems: "stretch",
            mb: { xs: 3, sm: 3.5 },
          }}
        >
          <Button
            variant="contained"
            startIcon={primaryAction.icon}
            onClick={() => navigate(primaryAction.path)}
            sx={{
              ...marketingBtnPrimarySx,
              flex: { sm: 1 },
              py: 1.35,
              fontSize: "0.95rem",
              maxWidth: { sm: 240 },
              mx: { xs: "auto", sm: 0 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {primaryAction.label}
          </Button>
          <Button
            variant="outlined"
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              ...marketingBtnSecondaryLightSx,
              flex: { sm: 1 },
              py: 1.35,
              fontSize: "0.95rem",
              maxWidth: { sm: 240 },
              mx: { xs: "auto", sm: 0 },
              width: { xs: "100%", sm: "auto" },
            }}
          >
            {t("goBack")}
          </Button>
        </Box>

        <Box
          sx={{
            width: "100%",
            pt: { xs: 2.5, sm: 3 },
            borderTop: `1px solid ${M.borderLight}`,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <Typography
            sx={{
              ...TYPE.caption,
              ...centeredText,
              color: M.muted,
              mb: 1.25,
            }}
          >
            {t("helpfulLinks")}
          </Typography>
          <Box
            sx={{
              display: "flex",
              gap: { xs: 0.25, sm: 0.75 },
              justifyContent: "center",
              alignItems: "center",
              flexWrap: "wrap",
              width: "100%",
            }}
          >
            {LINK_SETS[variant].map((link) => (
              <Button
                key={link.key}
                variant="text"
                onClick={() => navigate(link.path)}
                sx={{
                  color: M.primary,
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: { xs: "0.8125rem", sm: "0.875rem" },
                  px: { xs: 1, sm: 1.25 },
                  minWidth: 0,
                  "&:hover": { bgcolor: M.primaryDim },
                }}
              >
                {t(`links.${link.key}`)}
              </Button>
            ))}
          </Box>
        </Box>
      </Box>
    </Box>
  );
}
